/**
 * Org Service — políticas de acceso y gaps (T5, 27/10/2026). PURO: lo que DEBE pasar (org.policy)
 * cruzado con lo que ES (persona + asignaciones confirmadas) y lo que PASÓ (org.effective_access)
 * da, por persona, qué acceso le falta, cuál ya tiene y cuál le sobra. Sin I/O: lo usan el router,
 * la API, el MCP y los tests. Reglas:
 *
 *  - Una política aplica si la solución es la de la persona (día 1) o la de la asignación (al
 *    asignar), el puesto está vacío o coincide (sin tildes ni mayúsculas) y, para `on_assignment`,
 *    el cliente está vacío o es el de la asignación CONFIRMADA (las sugeridas no generan deberes).
 *  - `day1` espera (tool) una vez; `on_assignment` espera (tool, cliente) por asignación si la
 *    herramienta es `per_client`, o (tool) una sola vez si no lo es.
 *  - Un acceso efectivo cubre una expectativa si coincide la herramienta y, cuando la herramienta
 *    es por cliente, el cliente. `granted` = cubierto; `requested` = en trámite; `revoked` /
 *    `dangling` no cubren. Lo `granted` que ninguna política pide es un sobrante (`extra`).
 */
import { foldKey } from './text-normalize';
import type { OrgEffectiveAccessRow, OrgPolicyMoment, OrgPolicyRow, OrgToolRow } from './types/org';

export type OrgGapState = 'granted' | 'requested' | 'missing' | 'extra';

export interface OrgAccessExpectation {
  toolSlug: string;
  toolName: string;
  clientId: string | null;
  toolRole: string | null;
  moment: OrgPolicyMoment | null;
  approverEmail: string | null;
  /** Política que lo pide (null en un sobrante). */
  policyId: number | null;
  state: OrgGapState;
  /** Acceso efectivo que lo cubre o que sobra. */
  accessId: number | null;
  evidenceSystem: string | null;
  evidenceRef: string | null;
}

export interface OrgGapsSummary {
  granted: number;
  requested: number;
  missing: number;
  extra: number;
}

export interface OrgGapsInput {
  person: { employee_id: number; solution_slug: string | null; role_title: string | null };
  /** Asignaciones vigentes de la persona (se usan solo las confirmadas). */
  assignments: readonly { client_id: string; solution_slug: string; status: string }[];
  policies: readonly OrgPolicyRow[];
  tools: readonly Pick<OrgToolRow, 'slug' | 'name' | 'per_client'>[];
  access: readonly OrgEffectiveAccessRow[];
}

const roleMatches = (policyRole: string | null, personRole: string | null): boolean => !policyRole || foldKey(policyRole) === foldKey(personRole);

const accessKey = (toolSlug: string, clientId: string | null) => `${toolSlug}\u0000${clientId ?? ''}`;

/** Expectativas que generan las políticas para esta persona (sin mirar todavía los accesos). */
export function expectedAccess(input: Pick<OrgGapsInput, 'person' | 'assignments' | 'policies' | 'tools'>): Array<Omit<OrgAccessExpectation, 'state' | 'accessId' | 'evidenceSystem' | 'evidenceRef'>> {
  const tools = new Map(input.tools.map((t) => [t.slug, t]));
  const out = new Map<string, Omit<OrgAccessExpectation, 'state' | 'accessId' | 'evidenceSystem' | 'evidenceRef'>>();
  const confirmed = input.assignments.filter((a) => a.status === 'confirmed');
  // La política más específica (puesto y/o cliente fijados) gana sobre la genérica para la misma clave.
  const specificity = (p: OrgPolicyRow) => (p.role_title ? 2 : 0) + (p.client_id ? 1 : 0);
  const policies = [...input.policies].sort((a, b) => specificity(b) - specificity(a));
  for (const p of policies) {
    const tool = tools.get(p.tool_slug);
    if (!tool || !roleMatches(p.role_title, input.person.role_title)) continue;
    const base = { toolSlug: p.tool_slug, toolName: tool.name, toolRole: p.tool_role, moment: p.moment, approverEmail: p.approver_email, policyId: p.id };
    if (p.moment === 'day1') {
      if (p.solution_slug !== input.person.solution_slug || p.client_id) continue;
      const key = accessKey(p.tool_slug, null);
      if (!out.has(key)) out.set(key, { ...base, clientId: null });
      continue;
    }
    for (const a of confirmed) {
      if (a.solution_slug !== p.solution_slug) continue;
      if (p.client_id && p.client_id !== a.client_id) continue;
      const clientId = tool.per_client ? a.client_id : null;
      const key = accessKey(p.tool_slug, clientId);
      if (!out.has(key)) out.set(key, { ...base, clientId });
    }
  }
  return [...out.values()];
}

/** Expectativas × accesos efectivos → estado por expectativa + sobrantes. */
export function computeAccessGaps(input: OrgGapsInput): { items: OrgAccessExpectation[]; summary: OrgGapsSummary } {
  const tools = new Map(input.tools.map((t) => [t.slug, t]));
  const covering = new Map<string, OrgEffectiveAccessRow>();
  for (const a of input.access) {
    if (a.employee_id !== input.person.employee_id) continue;
    if (a.status !== 'granted' && a.status !== 'requested') continue;
    const tool = tools.get(a.tool_slug);
    const key = accessKey(a.tool_slug, tool?.per_client ? a.client_id : null);
    const prev = covering.get(key);
    if (!prev || (prev.status !== 'granted' && a.status === 'granted')) covering.set(key, a);
  }
  const used = new Set<string>();
  /**
   * Cobertura: la clave exacta; si no, para una herramienta por cliente, un acceso de esa herramienta
   * SIN cliente (credencial cuyo cliente no se resolvió) cubre la expectativa, y una expectativa de
   * día 1 sin cliente la cubre cualquier acceso de la herramienta. Así un mismo hecho no sale dos
   * veces (como "falta" y como "sobra"). Se prefiere lo `granted` sobre lo `requested`.
   */
  const findCover = (toolSlug: string, clientId: string | null): { key: string; row: OrgEffectiveAccessRow } | null => {
    const exact = accessKey(toolSlug, clientId);
    const e = covering.get(exact);
    if (e) return { key: exact, row: e };
    const candidates = [...covering.entries()].filter(([k, a]) => a.tool_slug === toolSlug && !used.has(k) && (clientId === null || a.client_id === null));
    if (!candidates.length) return null;
    candidates.sort(([, a], [, b]) => (a.status === 'granted' ? 0 : 1) - (b.status === 'granted' ? 0 : 1));
    return { key: candidates[0][0], row: candidates[0][1] };
  };
  const items: OrgAccessExpectation[] = expectedAccess(input).map((e) => {
    const found = findCover(e.toolSlug, e.clientId);
    const cov = found?.row;
    if (found) used.add(found.key);
    return {
      ...e,
      state: cov ? (cov.status === 'granted' ? 'granted' : 'requested') : 'missing',
      accessId: cov?.id ?? null,
      evidenceSystem: cov?.evidence_system ?? null,
      evidenceRef: cov?.evidence_ref ?? null,
    };
  });
  for (const [key, a] of covering) {
    if (used.has(key) || a.status !== 'granted') continue;
    items.push({
      toolSlug: a.tool_slug,
      toolName: tools.get(a.tool_slug)?.name ?? a.tool_slug,
      clientId: a.client_id,
      toolRole: null,
      moment: null,
      approverEmail: null,
      policyId: null,
      state: 'extra',
      accessId: a.id,
      evidenceSystem: a.evidence_system,
      evidenceRef: a.evidence_ref,
    });
  }
  const order: Record<OrgGapState, number> = { missing: 0, requested: 1, granted: 2, extra: 3 };
  items.sort((x, y) => order[x.state] - order[y.state] || x.toolName.localeCompare(y.toolName) || (x.clientId ?? '').localeCompare(y.clientId ?? ''));
  const summary: OrgGapsSummary = { granted: 0, requested: 0, missing: 0, extra: 0 };
  for (const i of items) summary[i.state]++;
  return { items, summary };
}

/** Slug de herramienta a partir de un nombre ("Meta Business Suite" → "meta-business-suite"). */
export function toolSlugFromName(name: string): string {
  return foldKey(name).replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
}

// ─── Evidencia desde el Vault (T5): credencial → herramienta del catálogo ───────

export interface VaultSecretEvidence {
  secretId: string;
  name: string;
  url: string | null;
  clientId: string | null;
  tags: string[];
  ownerEmail: string;
  needsRotation: boolean;
  /** Mails con permiso efectivo (grants por usuario + grupos de área y ad hoc expandidos). */
  emails: string[];
  /** Grupos de área con grant que Cognito no pudo expandir en esta corrida (evidencia PARCIAL para este secreto). */
  unresolvedGroups: string[];
}

const hostOf = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    return new URL(url.includes('://') ? url : `https://${url}`).hostname.replace(/^www\./, '').toLowerCase() || null;
  } catch {
    return null;
  }
};

/**
 * Qué herramienta del catálogo es una credencial del Vault: por tag `tool:<slug>`, por el host del
 * link de la herramienta igual al de la URL de la credencial, o por el nombre de la herramienta
 * contenido en el nombre de la credencial ("Meta Business — Nubank" → Meta Business Suite). Si
 * calza más de una por nombre, gana la de nombre más largo. Sin match → null (higiene).
 */
export function matchVaultSecretToTool<T extends Pick<OrgToolRow, 'slug' | 'name' | 'link'>>(secret: Pick<VaultSecretEvidence, 'name' | 'url' | 'tags'>, tools: readonly T[]): T | null {
  const byTag = secret.tags.map((t) => /^tool:([a-z0-9-]+)$/i.exec(t)?.[1]?.toLowerCase()).find(Boolean);
  if (byTag) {
    const t = tools.find((x) => x.slug === byTag);
    if (t) return t;
  }
  const host = hostOf(secret.url);
  if (host) {
    const byHost = tools.filter((t) => hostOf(t.link) === host);
    if (byHost.length === 1) return byHost[0];
  }
  const name = foldKey(secret.name);
  const byName = tools.filter((t) => {
    const k = foldKey(t.name);
    return k.length >= 3 && (name === k || name.includes(k) || name.includes(foldKey(t.slug.replace(/-/g, ' '))));
  });
  if (!byName.length) return null;
  return byName.sort((a, b) => b.name.length - a.name.length)[0];
}
